import { loadDurationOptions } from '@/components/reserve/ReserveFieldDuration/helpers';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import {
  DurationOption,
  RESERVE_TEXTS,
  SaveReservationData,
  getDurationOptionFromMinutes
} from '@/lib/utils-reserve';
import React, { useEffect, useState } from 'react';
import { FieldErrors, UseFormReturn } from 'react-hook-form';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useTimeInputMask } from '@gettactic/helpers/src/input/useTimeInputMask';
import { scrollToSelectedValue } from '../utils/scrollToSelectedValue';

type ReserveDurationProps = {
  form: UseFormReturn<SaveReservationData>;
  defaultValue: number;
  setDurationMin: React.Dispatch<React.SetStateAction<number>>;
  errors: FieldErrors<SaveReservationData>;
  durationOptions: DurationOption[];
  startTime: Date;
};
export default function ReserveFieldDuration({
  form,
  defaultValue,
  durationOptions: _durationOptions,
  setDurationMin,
  startTime
}: ReserveDurationProps) {
  const formValue = form.watch('duration')?.value;

  // State and useEffect are here to properly update the label of the select when the startTime changes
  const [selectedOption, setSelectedOption] = useState<DurationOption | null>(
    defaultValue ? getDurationOptionFromMinutes(startTime, defaultValue) : null
  );

  const [durationOptions, setDurationOptions] = useState<DurationOption[]>(
    () => {
      const newOptions = loadDurationOptions('', startTime, _durationOptions);
      const defaultValueFound = newOptions.find(
        (value) => value.value === defaultValue
      );
      const valuesToAdd = [
        defaultValueFound?.value,
        formValue,
        selectedOption?.value
      ].filter((value) => value !== undefined);
      valuesToAdd.forEach((value) => {
        if (!newOptions.find((option) => option.value === value)) {
          newOptions.push(getDurationOptionFromMinutes(startTime, value));
        }
      });
      return newOptions.sort((a, b) => a.value - b.value);
    }
  );

  const mask = useTimeInputMask();

  useEffect(() => {
    const newOptions = loadDurationOptions('', startTime, _durationOptions);
    if (defaultValue) {
      const defaultValueFound = newOptions.find(
        (value) => value.value === defaultValue
      );
      if (!defaultValueFound) {
        newOptions.push(getDurationOptionFromMinutes(startTime, defaultValue));
      }
    }
    if (formValue) {
      const formValueFound = newOptions.find(
        (value) => value.value === formValue
      );
      if (!formValueFound) {
        const newOption = getDurationOptionFromMinutes(startTime, formValue);
        newOptions.push(newOption);
        setSelectedOption(newOption);
      }
    }
    setDurationOptions(newOptions.sort((a, b) => a.value - b.value));
  }, [startTime]);

  useEffect(() => {
    if (selectedOption) {
      setSelectedOption(
        durationOptions.find(
          (option) => option.value === selectedOption?.value
        ) || null
      );
    }
  }, [selectedOption?.value, durationOptions]);

  // Function to scroll to selected value when menu opens
  const handleMenuOpen = () => {
    scrollToSelectedValue(selectedOption);
  };
  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (element) => {
    if (selectedOption) {
      // This works for regular Select but not Async
      element.currentTarget.value = selectedOption.label;
    }
  };
  return (
    <>
      <FormField
        control={form.control}
        name="duration"
        rules={{ required: true }}
        defaultValue={selectedOption || undefined}
        render={({ field }) => (
          <FormItem>
            <AsyncSelect
              onFocus={handleFocus}
              menuPortalTarget={document.body}
              menuPosition={'fixed'}
              onMenuOpen={handleMenuOpen}
              formatOptionLabel={(option) => (
                <div>
                  <div>{option.label}</div>
                  <div className={'text-muted-foreground'}>
                    {(option as DurationOption).durationLabel}
                  </div>
                </div>
              )}
              components={{
                SingleValue: (props) => (
                  <components.SingleValue {...props}>
                    {props.data.label}
                  </components.SingleValue>
                ),
                Input: (props) => (
                  <components.Input {...props} size={undefined} {...mask} />
                )
              }}
              onChange={(d, event) => {
                field.onChange(d, event);
                if (!d) {
                  setSelectedOption(null);
                  return;
                }
                setDurationMin(d.value);
                setSelectedOption(d);

                if (!durationOptions.find((opt) => opt.value === d.value)) {
                  setDurationOptions((prev) =>
                    [...prev, d].sort((a, b) => a.value - b.value)
                  );
                }
              }}
              value={selectedOption}
              loadOptions={(filter, callback) =>
                callback(
                  loadDurationOptions(filter, startTime, durationOptions)
                )
              }
              defaultOptions={durationOptions}
              placeholder={RESERVE_TEXTS.selectDuration}
              classNamePrefix="react-select"
            />
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
